import { ActivatedRouteSnapshot, UrlTree, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { Inject, Injectable } from '@angular/core';
import { map, catchError, switchMap } from 'rxjs/operators';
import { isNullOrUndefined } from '@common/util';
import { AuthService } from '@common/services/auth.service';
import { EnjpCommunityService } from '../services/community.service';
import { EnjpCommunity, CommunityMode } from '../model/community';
import { User } from '@common/model/user';
import { DEFAULT_COMMUNITY } from '@common/app/app.config';
import { DOCUMENT } from '@angular/common';

@Injectable()
export class CommunityGuard  {
    constructor(
        private authService: AuthService,
        private router: Router,
        private communityService: EnjpCommunityService,
        @Inject(DEFAULT_COMMUNITY) private defaultCommunity: string,
        @Inject(DOCUMENT) private document: Document,
    ) {}

    private redirectToOrigin():
        | Observable<boolean | UrlTree>
        | Promise<boolean | UrlTree> {
        // Exclude localhost because of unit tests
        if (
            this.document?.location &&
            document.location.origin.indexOf('localhost') === -1
        ) {
            const redirectUrl = document.location.origin;
            // Hardcoded redirection to origin (ex. https://app-jp-demo.ichoosr.com or https://group-buy.jp)
            document.location.href = redirectUrl;
            return of(false);
        }
        return this.router.navigate([
            this.defaultCommunity,
            'error',
            'community',
        ]);
    }

    canActivate(
        route: ActivatedRouteSnapshot,
    ):
        | Observable<boolean | UrlTree>
        | Promise<boolean | UrlTree>
        | boolean
        | UrlTree {
        return this.communityService.fetchCommunity(route.params).pipe(
            // Check that community exists and if doesn't, redirect to default
            switchMap((community) =>
                !isNullOrUndefined(community)
                    ? of(community)
                    : this.redirectToOrigin(),
            ),

            // Check that community is hidden and if it is, check also user privileges
            switchMap((community) =>
                (community as EnjpCommunity)?.communityMode ===
                CommunityMode.Hidden
                    ? this.authService.user$
                    : of(true),
            ),

            map((input) => {
                const isBoolean = input === true || input === false;
                if (isBoolean) {
                    return input as boolean;
                }

                const allowedRoles: string[] = [
                    'developer',
                    'business_admin',
                    'company_admin',
                ];
                return allowedRoles.includes((input as User)?.role);
            }),

            switchMap((shouldPass: boolean) =>
                shouldPass ? of(true) : this.redirectToOrigin(),
            ),

            catchError(() => this.redirectToOrigin()),
        );
    }
}
